import ProfilesMatchingRolesController from "PFApp/profiles/matching_roles/profile_matching_roles_controller";
import ProfileShowController from "PFApp/profiles/show/profile_show_controller";
import NotFoundRoute from "PFApp/routes/not_found_route";
import {
  EnsureLoggedInAndRegistered,
  EnsureProfilePermission,
  EnsureWorkflowPermission,
  WithLayout
} from "PFApp/routes/route_helpers";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag, PermissionRule } from "PFTypes";
import { lazy, ReactNode } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";

import ProfileEditController from "../profiles/edit/profile_edit_controller";
import { ProfilesPageControllerWrapper } from "../profiles/profiles_page/profiles_page_controller";

const ProfileShowRouteGuard = () => {
  const { id } = useParams();

  return (
    <EnsureProfilePermission permittedTo={PermissionRule.ProfileList} enabled={id !== "me"}>
      <ProfileShowController />
    </EnsureProfilePermission>
  );
};

type CvExportsRouteGuardProps = {
  children: ReactNode;
};

const CvExportsRouteGuard = ({ children }: CvExportsRouteGuardProps) => {
  const isEnabled = useIsFeatureEnabled();

  return isEnabled(FeatureFlag.CVExportLegacyFlow) ? <NotFoundRoute /> : children;
};

const CVExportsPageController = lazy(() =>
  import("../profiles/cv_exports/cv_exports_page_controller").then(({ CVExportsPageController }) => ({
    default: CVExportsPageController
  }))
);

const CVExportsSummaryPageController = lazy(() =>
  import("../profiles/cv_exports_summary/cv_exports_summary_page_controller").then(
    ({ CVExportsSummaryPageController }) => ({ default: CVExportsSummaryPageController })
  )
);

export const ProfilesRoutes = () => (
  <EnsureLoggedInAndRegistered>
    <WithLayout>
      <Routes>
        <Route
          path="cv_exports/summary"
          element={
            <CvExportsRouteGuard>
              <CVExportsSummaryPageController />
            </CvExportsRouteGuard>
          }
        />
        <Route
          path="cv_exports"
          element={
            <CvExportsRouteGuard>
              <CVExportsPageController />
            </CvExportsRouteGuard>
          }
        />
        <Route path="me/suggested_skills" element={<Navigate to="../me/summary/skills" replace />} />
        <Route path="edit/:section" element={<ProfileEditController />} />
        <Route
          path=":id/matching_roles"
          element={
            <EnsureWorkflowPermission>
              <ProfilesMatchingRolesController />
            </EnsureWorkflowPermission>
          }
        />
        <Route path=":id" element={<ProfileShowRouteGuard />}>
          <Route path=":section" element={<ProfileShowController />}>
            <Route path=":additionalSection" element={<ProfileShowController />} />
          </Route>
        </Route>
        <Route
          path="/"
          element={
            <EnsureProfilePermission permittedTo={PermissionRule.ProfileList}>
              <ProfilesPageControllerWrapper />
            </EnsureProfilePermission>
          }
        />
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </WithLayout>
  </EnsureLoggedInAndRegistered>
);
